import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MapPin, Maximize2, Calendar, X, ChevronLeft, ChevronRight, Play } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

const tabs = ["Exterior", "Interior", "Walkthrough"] as const;
type Tab = typeof tabs[number];

type ProjectImage = { id: string; src_url: string; alt: string | null; section: string; sort_order: number };
type ProjectVideo = { id: string; src_url: string; title: string | null };

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const staggerItem = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
};

const ProjectDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<Tab>("Exterior");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { data: project, isLoading } = useQuery({
    queryKey: ["public-project", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("slug", slug!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  const { data: images = [] } = useQuery({
    queryKey: ["public-project-images", project?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_images")
        .select("*")
        .eq("project_id", project!.id)
        .order("sort_order");
      if (error) throw error;
      return data as ProjectImage[];
    },
    enabled: !!project?.id,
  });

  const { data: videos = [] } = useQuery({
    queryKey: ["public-project-videos", project?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_videos")
        .select("*")
        .eq("project_id", project!.id);
      if (error) throw error;
      return data as ProjectVideo[];
    },
    enabled: !!project?.id,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading…</div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <h1 className="text-3xl font-bold text-foreground">Project Not Found</h1>
          <Button asChild variant="outline">
            <Link to="/projects"><ArrowLeft size={16} className="mr-2" /> Back to Projects</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const tabImages: Record<Tab, ProjectImage[]> = {
    Exterior: images.filter((i) => i.section === "exterior"),
    Interior: images.filter((i) => i.section === "interior"),
    Walkthrough: images.filter((i) => i.section === "walkthrough"),
  };

  const currentImages = tabImages[activeTab];
  const allImages = [...tabImages.Exterior, ...tabImages.Interior, ...tabImages.Walkthrough];

  const openLightbox = (img: ProjectImage) => {
    const idx = allImages.findIndex((i) => i.id === img.id);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  };

  const navigateLightbox = (dir: number) => {
    setLightboxIndex((prev) => (prev + dir + allImages.length) % allImages.length);
  };

  return (
    <Layout>
      <SEOHead
        title={`${project.title} — ${project.category} Project`}
        description={project.overview?.slice(0, 155) || `${project.title} — a ${project.category.toLowerCase()} project by Dhivisha Architects in ${project.location || 'Tamil Nadu'}.`}
        path={`/projects/${slug}`}
        image={project.thumbnail_url || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: project.title,
          genre: project.category,
          description: project.overview || undefined,
          image: project.thumbnail_url || undefined,
          url: `https://dhivishaarchitects.com/projects/${slug}`,
          locationCreated: project.location ? { "@type": "Place", name: project.location } : undefined,
          creator: { "@id": "https://dhivishaarchitects.com/#organization" },
        }}
      />
      <section className="relative h-[50vh] md:h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {project.thumbnail_url ? (
            <img src={project.thumbnail_url} alt={project.title} className="w-full h-full object-cover" loading="eager" />
          ) : (
            <div className="w-full h-full bg-muted" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 pb-10 w-full">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Projects
          </Link>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-5xl font-bold text-primary-foreground mb-2"
          >
            {project.title}
          </motion.h1>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-block text-xs uppercase tracking-widest text-primary font-semibold bg-secondary/60 px-3 py-1 rounded"
          >
            {project.category}
          </motion.span>
        </div>
      </section>

      <section className="py-12 bg-muted">
        <motion.div
          className="max-w-6xl mx-auto px-4 grid md:grid-cols-[2fr_1fr] gap-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-4">Project Overview</h2>
            <p className="text-muted-foreground leading-relaxed">{project.overview}</p>
          </div>
          <div className="space-y-4">
            {project.location && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin size={16} className="text-primary shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Location</p>
                  <p className="text-foreground font-medium">{project.location}</p>
                </div>
              </div>
            )}
            {project.area && (
              <div className="flex items-center gap-3 text-sm">
                <Maximize2 size={16} className="text-primary shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Area</p>
                  <p className="text-foreground font-medium">{project.area}</p>
                </div>
              </div>
            )}
            {project.year && (
              <div className="flex items-center gap-3 text-sm">
                <Calendar size={16} className="text-primary shrink-0" />
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Year</p>
                  <p className="text-foreground font-medium">{project.year}</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-2 mb-10">
            {tabs.map((tab) => {
              const count = tabImages[tab].length;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider rounded transition-all active:scale-95 ${
                    activeTab === tab
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                  }`}
                >
                  {tab === "Walkthrough" ? "Execution Process & Walkthrough" : tab} ({count})
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {currentImages.length > 0 ? (
                <motion.div
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {currentImages.map((img) => (
                    <motion.div
                      key={img.id}
                      variants={staggerItem}
                      whileTap={{ scale: 0.98 }}
                      className="group relative overflow-hidden rounded cursor-pointer border border-border hover:border-primary transition-colors"
                      onClick={() => openLightbox(img)}
                    >
                      <img
                        src={img.src_url}
                        alt={img.alt || ""}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-secondary/0 group-hover:bg-secondary/40 transition-colors flex items-center justify-center">
                        <Maximize2 size={24} className="text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <div className="text-center py-20 text-muted-foreground">
                  <p className="text-lg">No {activeTab.toLowerCase()} images available yet.</p>
                  <p className="text-sm mt-1">Images will be uploaded soon.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {videos.length > 0 && (
        <section className="py-16 bg-muted">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-foreground mb-8 flex items-center gap-3">
              <Play size={20} className="text-primary" /> Project Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map((video) => (
                <motion.div
                  key={video.id}
                  className="rounded overflow-hidden border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="aspect-video">
                    <iframe
                      src={video.src_url}
                      title={video.title || "Video"}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 bg-card">
                    <p className="text-sm font-medium text-foreground">{video.title}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <motion.section
        className="py-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-3">Interested in a similar project?</h2>
          <p className="text-muted-foreground mb-6">Get in touch and let's bring your vision to life.</p>
          <Button asChild className="px-8 active:scale-95 transition-transform">
            <a
              href={`https://wa.me/919994849332?text=${encodeURIComponent(`Hi, I'm interested in a project similar to "${project.title}". Can we discuss?`)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us on WhatsApp
            </a>
          </Button>
        </div>
      </motion.section>

      <AnimatePresence>
        {lightboxOpen && allImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-secondary/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <button
              className="absolute top-6 right-6 text-primary-foreground/70 hover:text-primary-foreground z-10"
              onClick={() => setLightboxOpen(false)}
            >
              <X size={28} />
            </button>

            {allImages.length > 1 && (
              <>
                <button
                  className="absolute left-4 md:left-8 text-primary-foreground/70 hover:text-primary-foreground z-10"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                >
                  <ChevronLeft size={36} />
                </button>
                <button
                  className="absolute right-4 md:right-8 text-primary-foreground/70 hover:text-primary-foreground z-10"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                >
                  <ChevronRight size={36} />
                </button>
              </>
            )}

            <motion.img
              key={allImages[lightboxIndex].id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={allImages[lightboxIndex].src_url}
              alt={allImages[lightboxIndex].alt || ""}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded"
              onClick={(e) => e.stopPropagation()}
            />

            <p className="absolute bottom-6 text-primary-foreground/60 text-sm">
              {lightboxIndex + 1} / {allImages.length} — {allImages[lightboxIndex].alt}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default ProjectDetail;
